import { useRef, useState } from "react";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import Icon from "@mui/material/Icon";
import PropTypes from "prop-types";
import { List, ListItem, ListItemText, ListItemSecondaryAction, IconButton, Box, FormControlLabel, Checkbox, RadioGroup, Radio, Grid } from "@mui/material";
import useAPI from "utils/useApi";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { DASHBOARD, accentBtnSx } from "../../../colors";

export default function ActionRaspunsOficial({
    reportId,
    resolvedForm,
    files,
    setFiles,
    message,
    setMessage,
    isStatusSolved,
    onUploadSuccess,
    refreshDetails,
    incidentMotivation,
    setIncidentMotivation,
    incidentLocation,
    setIncidentLocation,
    offlineCategories,
    setOfflineCategories,
    formActionsData,
}) {
    const client = useAPI();
    const fileInputRef = useRef();
    const [deleting, setDeleting] = useState(false);

    // Helper: get filename from url
    const getFileName = (url) => url.split("/").pop();

    // Handle authenticated document download
    const handleDocumentDownload = async (fileObj) => {
        try {
            if (fileObj.file) {
                // Use authenticated API call to get the document
                const response = await client.getDocument(fileObj.file);
                
                // Get content type from headers with fallback
                const contentType = response.headers?.['content-type'] || 
                                   response.headers?.['Content-Type'] || 
                                   'application/octet-stream';
                
                // Create blob from response data
                const blob = new Blob([response.data], { type: contentType });
                const url = window.URL.createObjectURL(blob);
                
                // Create download link
                const link = document.createElement('a');
                link.href = url;
                link.download = fileObj.filename || getFileName(fileObj.file) || "document";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                
                // Clean up the URL object
                window.URL.revokeObjectURL(url);
            }
        } catch (error) {
            console.error('Error downloading document:', error);
            // Fallback to direct link on error
            window.open(fileObj.file, '_blank');
        }
    };

    // Build the extra fields for create/update
    const buildExtraFields = () => ({
        incident_types: incidentMotivation,
        incident_place_type: incidentLocation,
        injury_types: incidentLocation === "offline" ? offlineCategories : [],
    });

    // Submit handler: create or update
    const handleSubmit = async () => {
        if (!message && files.length === 0) return;
        const extraFields = buildExtraFields();
        try {
            if (!resolvedForm) {
                // CREATE: send all files, message, and extra fields
                const formData = new FormData();
                formData.append("resolution_details", message);
                formData.append("incident_place_type", extraFields.incident_place_type);
                extraFields.incident_types.forEach((type) => {
                    formData.append("incident_types", type);
                });
                extraFields.injury_types.forEach((type) => {
                    formData.append("injury_types", type);
                });
                files.forEach((file) => {
                    formData.append("files", file);
                });
                await client.registerResolvedComplaintForm(reportId, formData);
                setFiles([]);
                if (refreshDetails) await refreshDetails();
                toast.success("Răspunsul oficial a fost trimis cu succes.");
            } else {
                // UPDATE: patch message + extra fields, upload new files
                await client.updateResolvedMessage(resolvedForm.id, {
                    resolution_details: message,
                    incident_types: extraFields.incident_types,
                    incident_place_type: extraFields.incident_place_type,
                    injury_types: extraFields.injury_types,
                });
                
                let newFiles = [];
                for (const file of files) {
                    const resp = await client.addResolvedFile(resolvedForm.id, file);
                    newFiles.push(resp);
                }

                if (newFiles.length > 0) {
                    resolvedForm.files = [...(resolvedForm.files || []), ...newFiles];
                }
                setFiles([]);
                if (refreshDetails) await refreshDetails();
                toast.success("Raspunsul a fost modificat cu succes");
            }
        } catch (err) {
            const data = err.response?.data;
            if (data) {
                if (typeof data.detail === "string") {
                    toast.error(data.detail);
                } else if (Array.isArray(data.detail)) {
                    data.detail.forEach((msg) => toast.error(msg));
                } else if (typeof data === "string") {
                    toast.error(data);
                } else if (typeof data === "object") {
                    Object.entries(data).forEach(([field, messages]) => {
                        if (Array.isArray(messages)) {
                            messages.forEach((msg) => toast.error(`${field}: ${msg}`));
                        } else {
                            toast.error(`${field}: ${messages}`);
                        }
                    });
                } else {
                    toast.error("A apărut o eroare la trimiterea răspunsului.");
                }
            } else {
                toast.error("A apărut o eroare la trimiterea răspunsului.");
            }
        }
    };

    const handleConfirmSubmit = async () => {
        // Prevent update if nothing has changed
        const backendMessage = resolvedForm?.resolution_details || "";
        const filesUnchanged = files.length === 0;
        const messageUnchanged = message === backendMessage;

        const backendIncidentTypes = resolvedForm?.incident_types || [];
        const backendPlaceType = resolvedForm?.incident_place_type || "";
        const backendInjuryTypes = resolvedForm?.injury_types || [];

        const incidentTypesUnchanged = JSON.stringify([...incidentMotivation].sort()) === JSON.stringify([...backendIncidentTypes].sort());
        const placeTypeUnchanged = incidentLocation === backendPlaceType;
        const injuryTypesUnchanged = JSON.stringify([...offlineCategories].sort()) === JSON.stringify([...backendInjuryTypes].sort());

        if (resolvedForm && filesUnchanged && messageUnchanged && incidentTypesUnchanged && placeTypeUnchanged && injuryTypesUnchanged) {
            toast.error("Nu există modificări de trimis la răspunsul oficial.");
            return;
        }
        const result = await Swal.fire({
            title: "Ești sigur?",
            text: resolvedForm ? "Vrei să actualizezi răspunsul oficial?" : "Vrei să încarci și să trimiți răspunsul final?",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: resolvedForm ? "Da, actualizează!" : "Da, trimite!",
            cancelButtonText: "Anulează"
        });
        if (result.isConfirmed) {
            handleSubmit();
        }
    };

    const handleDeleteFile = async (fileId) => {
        if (!resolvedForm) return;
        setDeleting(true);
        try {
            await client.deleteResolvedFile(fileId);
            // Remove from UI immediately
            if (resolvedForm.files) {
                resolvedForm.files = resolvedForm.files.filter(f => f.id !== fileId);
            }
            if (onUploadSuccess) onUploadSuccess();
            toast.success("Fișierul a fost șters cu succes.");
        } catch (err) {
            console.error("Error deleting file:", err);
        }
        setDeleting(false);
    };

    const handleBoxClick = () => {
        if (!isStatusSolved) {
            fileInputRef.current.click();
        }
    };

    const handleFilesChange = (e) => {
        const newFiles = Array.from(e.target.files);
        const uniqueFiles = newFiles.filter(
            nf => !files.some(f => f.name === nf.name && f.size === nf.size)
        );
        setFiles([...files, ...uniqueFiles]);
        e.target.value = null;
    };

    const handleRemoveFile = (index) => {
        setFiles(files.filter((_, i) => i !== index));
    };

    const uploadedFiles = resolvedForm?.files || [];

    return (
        <SoftBox 
            p={2.5}
            mt={2}
            border="1px solid #eee"
            borderRadius="lg"
            boxShadow="0 2px 12px 0 rgba(0,0,0,0.05)"
            bgcolor="white"
            position="relative"
            overflow="hidden"
        >
            <SoftBox position="absolute" top={0} left={0} width="4px" height="100%" bgcolor="info.main" />
            <SoftTypography variant="subtitle2" fontWeight="medium" mb={2} pl={1}>
                <Icon fontSize="small" sx={{ verticalAlign: "middle", mr: 1 }}>description</Icon>
                Răspuns oficial
            </SoftTypography>
            <SoftBox 
                p={1.5}
                border="1px dashed #ccc"
                borderRadius="md"
                bgcolor="#f8f9fa"
                display="flex"
                alignItems="center"
                justifyContent="center"
                sx={{ 
                    cursor: isStatusSolved ? "not-allowed" : "pointer", 
                    transition: "all 0.2s", 
                    "&:hover": { bgcolor: "#f0f0f0", borderColor: "#aaa" } 
                }}
                onClick={handleBoxClick}
            >
                <Icon fontSize="small" color="info" sx={{ mr: 1 }}>cloud_upload</Icon>
                <SoftTypography variant="caption" color="text">
                    {isStatusSolved ? "Încărcarea dezactivată" : "Încarcă documente sau trage fișierele aici"}
                </SoftTypography>
                <input
                    type="file"
                    multiple
                    hidden
                    ref={fileInputRef}
                    onChange={handleFilesChange}
                    disabled={isStatusSolved}
                />
            </SoftBox>
            {/* Uploaded files */}
            {uploadedFiles.length > 0 && (
                <Box mt={2}>
                    <SoftTypography variant="caption" fontWeight="bold">Fișiere încărcate:</SoftTypography>
                    <List dense>
                        {uploadedFiles.map((fileObj) => (
                            <ListItem key={fileObj.id}>
                                <ListItemText
                                    primary={
                                        <Box
                                            component="span"
                                            onClick={() => handleDocumentDownload(fileObj)}
                                            sx={{
                                                cursor: 'pointer',
                                                color: 'primary.main',
                                                textDecoration: 'underline',
                                                '&:hover': {
                                                    color: 'primary.dark',
                                                }
                                            }}
                                        >
                                            {getFileName(fileObj.file)}
                                        </Box>
                                    }
                                />
                                {!isStatusSolved && (
                                    <ListItemSecondaryAction>
                                        <IconButton edge="end" color="error" disabled={deleting} onClick={() => handleDeleteFile(fileObj.id)}>
                                            <Icon>delete</Icon>
                                        </IconButton>
                                    </ListItemSecondaryAction>
                                )}
                            </ListItem>
                        ))}
                    </List>
                </Box>
            )}
            {/* New files to be uploaded */}
            {files.length > 0 && (
                <Box mt={2}>
                    <SoftTypography variant="caption" fontWeight="bold">Fișiere de încărcat:</SoftTypography>
                    <List dense>
                        {files.map((file, idx) => (
                            <ListItem key={file.name + file.size}>
                                <ListItemText
                                    primary={file.name}
                                    secondary={`${(file.size / 1024).toFixed(1)} KB`}
                                />
                                {!isStatusSolved && (
                                    <ListItemSecondaryAction>
                                        <IconButton edge="end" onClick={() => handleRemoveFile(idx)}>
                                            <Icon color="error">delete</Icon>
                                        </IconButton>
                                    </ListItemSecondaryAction>
                                )}
                            </ListItem>
                        ))}
                    </List>
                </Box>
            )}
            <Box mt={2}>
                <textarea
                    style={{
                        width: "100%",
                        minHeight: 60,
                        height: 160,
                        borderRadius: 8,
                        border: "1px solid #ccc",
                        padding: 8,
                        fontFamily: "inherit",
                        fontSize: "1rem",
                        resize: "vertical",
                        marginBottom: 8,
                    }}
                    placeholder="Introduceți detalii despre soluționarea cazului..."
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                    disabled={isStatusSolved}
                />
                
                {/* Tip incident/motivație și Loc incident pe aceeași linie */}
                <SoftBox mt={2.5} mb={2}>
                    <Grid container spacing={2}>
                        {/* Tip incident/motivație */}
                        <Grid item xs={12} sm={6}>
                            <SoftBox p={2} sx={{ bgcolor: "#f8f9fa", borderRadius: "10px", border: "1px solid #e9ecef", height: "100%" }}>
                                <SoftTypography variant="caption" fontWeight="bold" sx={{ color: "#344767", textTransform: "uppercase", letterSpacing: "0.8px", fontSize: "0.7rem", display: "block", mb: 1.5 }}>
                                    Tip incident/motivație
                                </SoftTypography>
                                <SoftBox display="flex" flexDirection="column" gap={0.75}>
                                    {formActionsData?.incident_types?.map((item) => (
                                        <FormControlLabel
                                            key={item.value}
                                            control={
                                                <Checkbox
                                                    checked={incidentMotivation.includes(item.value)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setIncidentMotivation([...incidentMotivation, item.value]);
                                                        } else {
                                                            setIncidentMotivation(incidentMotivation.filter((m) => m !== item.value));
                                                        }
                                                    }}
                                                    disabled={isStatusSolved}
                                                    size="small"
                                                    sx={{ color: DASHBOARD.blue, "&.Mui-checked": { color: DASHBOARD.blue } }}
                                                />
                                            }
                                            label={<SoftTypography variant="body2" sx={{ fontSize: "0.875rem", color: "#344767", fontWeight: 500 }}>{item.label}</SoftTypography>}
                                        />
                                    ))}
                                </SoftBox>
                            </SoftBox>
                        </Grid>

                        {/* Loc incident */}
                        <Grid item xs={12} sm={6}>
                            <SoftBox p={2} sx={{ bgcolor: "#f8f9fa", borderRadius: "10px", border: "1px solid #e9ecef", height: "100%" }}>
                                <SoftTypography variant="caption" fontWeight="bold" sx={{ color: "#344767", textTransform: "uppercase", letterSpacing: "0.8px", fontSize: "0.7rem", display: "block", mb: 1.5 }}>
                                    Loc incident
                                </SoftTypography>
                                <RadioGroup
                                    value={incidentLocation}
                                    onChange={(e) => {
                                        setIncidentLocation(e.target.value);
                                        if (e.target.value !== "offline") {
                                            setOfflineCategories([]);
                                        }
                                    }}
                                    sx={{ "& .MuiRadio-root": { color: DASHBOARD.blue, "&.Mui-checked": { color: DASHBOARD.blue } } }}
                                >
                                    {formActionsData?.incident_place_types?.map((item) => (
                                        <FormControlLabel
                                            key={item.value}
                                            value={item.value}
                                            control={<Radio size="small" disabled={isStatusSolved} />}
                                            label={<SoftTypography variant="body2" sx={{ fontSize: "0.875rem", color: "#344767", fontWeight: 500 }}>{item.label}</SoftTypography>}
                                        />
                                    ))}
                                </RadioGroup>
                            </SoftBox>
                        </Grid>
                    </Grid>

                    {/* Sub-opțiuni pentru offline - apare sub ambele secțiuni */}
                    {incidentLocation === "offline" && (
                        <SoftBox mt={2.5} p={2.5} sx={{ bgcolor: "#ffffff", borderRadius: "10px", border: "1px solid #dee2e6" }}>
                            <SoftTypography variant="caption" fontWeight="bold" sx={{ color: "#8392ab", fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.8px", display: "block", mb: 1.5 }}>
                                Categorii:
                            </SoftTypography>
                            <SoftBox display="flex" flexDirection="row" flexWrap="wrap" gap={2}>
                                {formActionsData?.injury_types?.map((item) => (
                                    <FormControlLabel
                                        key={item.value}
                                        control={
                                            <Checkbox
                                                checked={offlineCategories.includes(item.value)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setOfflineCategories([...offlineCategories, item.value]);
                                                    } else {
                                                        setOfflineCategories(offlineCategories.filter((c) => c !== item.value));
                                                    }
                                                }}
                                                disabled={isStatusSolved}
                                                size="small"
                                                sx={{ color: DASHBOARD.blue, "&.Mui-checked": { color: DASHBOARD.blue } }}
                                            />
                                        }
                                        label={<SoftTypography variant="body2" sx={{ fontSize: "0.875rem", color: "#344767", fontWeight: 500 }}>{item.label}</SoftTypography>}
                                    />
                                ))}
                            </SoftBox>
                        </SoftBox>
                    )}
                </SoftBox>
                
                <SoftButton 
                    variant="gradient"
                    color="info"
                    size="small"
                    fullWidth
                    sx={{ mt: 1.5, ...accentBtnSx }}
                    disabled={isStatusSolved}
                    onClick={handleConfirmSubmit}
                >
                    <Icon fontSize="small" sx={{ mr: 0.5 }}>check_circle</Icon>
                    {resolvedForm ? "Actualizează răspunsul" : "Încarcă și trimite răspunsul final"}
                </SoftButton>
            </Box>
        </SoftBox>
    );
}

ActionRaspunsOficial.propTypes = {
    reportId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    resolvedForm: PropTypes.object,
    files: PropTypes.array.isRequired,
    setFiles: PropTypes.func.isRequired,
    message: PropTypes.string.isRequired,
    setMessage: PropTypes.func.isRequired,
    isStatusSolved: PropTypes.bool.isRequired,
    onUploadSuccess: PropTypes.func,
    refreshDetails: PropTypes.func,
    incidentMotivation: PropTypes.array.isRequired,
    setIncidentMotivation: PropTypes.func.isRequired,
    incidentLocation: PropTypes.string.isRequired,
    setIncidentLocation: PropTypes.func.isRequired,
    offlineCategories: PropTypes.array.isRequired,
    setOfflineCategories: PropTypes.func.isRequired,
    formActionsData: PropTypes.object,
};